// PATH: src/components/questReview/QuestView.tsx
import * as React from "react";
import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/CheckCircleRounded";
import type { QuestBody } from "../../api/questReview/questReview";

/**
 * Une quête telle que l'élève la lit, plus sa clé : l'humain doit voir
 * l'exercice, pas son JSON. Une présentation par forme servie (QCM, tableau,
 * appariement, tri, frise, remise en ordre, analogie, carte de concepts) ; une
 * forme inconnue retombe sur ses options et sa spec brute, jamais sur un écran
 * vide.
 */

type Rec = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const Text: React.FC<{ children: React.ReactNode; muted?: boolean; variant?: "body1" | "body2" }> = ({
  children,
  muted,
  variant = "body2",
}) => (
  <Typography variant={variant} dir="auto" color={muted ? "text.secondary" : undefined} sx={{ whiteSpace: "pre-wrap" }}>
    {children}
  </Typography>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="overline" color="text.secondary">
      {title}
    </Typography>
    {children}
  </Box>
);

const Options: React.FC<{ options: Rec[] }> = ({ options }) => (
  <Stack spacing={0.75} sx={{ mt: 1 }}>
    {options.map((o) => (
      <Box
        key={o.option_id}
        sx={{
          p: 1,
          borderRadius: 1,
          border: 1,
          borderColor: o.is_correct ? "success.main" : "divider",
          bgcolor: o.is_correct ? "action.hover" : undefined,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography className="mono" color="text.secondary" sx={{ minWidth: 18 }}>
            {o.option_id}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Text>{o.payload}</Text>
            {o.rationale && <Text muted>{o.rationale}</Text>}
          </Box>
          {o.is_correct && <CheckIcon color="success" fontSize="small" titleAccess="Bonne réponse" />}
        </Stack>
      </Box>
    ))}
  </Stack>
);

const byOrder = <T extends Rec>(items: T[], order: string[] | undefined, key: string): T[] =>
  order && order.length ? order.map((id) => items.find((i) => i[key] === id)).filter((x): x is T => Boolean(x)) : items;

/** La clé de la forme, lisible. */
const Spec: React.FC<{ step: Rec }> = ({ step }) => {
  const spec: Rec = step.expected_answer_spec || {};
  switch (step.render_type) {
    case "gap_fill_table":
      return (
        <Box sx={{ overflowX: "auto", mt: 1 }}>
          <Table size="small">
            {Array.isArray(spec.headers) && (
              <TableHead>
                <TableRow>
                  {spec.headers.map((h: string, i: number) => (
                    <TableCell key={i} dir="auto">
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {(spec.rows || []).map((row: Rec, r: number) => (
                <TableRow key={r}>
                  {(row.cells || []).map((cell: Rec, c: number) => (
                    <TableCell key={c} dir="auto">
                      {cell.is_blank ? (
                        <Chip size="small" color="success" variant="outlined" label={spec.answers?.[cell.blank_id] ?? "?"} />
                      ) : (
                        cell.value
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {Array.isArray(spec.choices) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} dir="auto">
              Choix proposés : {spec.choices.join(" · ")}
            </Typography>
          )}
        </Box>
      );
    case "pair_matching":
      return (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {(spec.pairs || []).map((p: Rec) => (
            <Box key={p.id}>
              <Text>
                <b>{p.left}</b> ↔ {p.right}
              </Text>
              {p.rationale && <Text muted>{p.rationale}</Text>}
            </Box>
          ))}
        </Stack>
      );
    case "category_sort":
      return (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {(spec.categories || []).map((c: Rec) => (
            <Box key={c.id}>
              <Text>
                <b>{c.name}</b>
              </Text>
              {(spec.items || [])
                .filter((i: Rec) => spec.answer?.[i.id] === c.id)
                .map((i: Rec) => (
                  <Text key={i.id} muted>
                    · {i.label}
                    {i.rationale ? ` — ${i.rationale}` : ""}
                  </Text>
                ))}
            </Box>
          ))}
        </Stack>
      );
    case "timeline_builder":
      return (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {byOrder(spec.events || [], spec.correct_order, "id").map((e: Rec, i: number) => (
            <Text key={e.id}>
              {i + 1}. {e.date ? `${e.date} — ` : ""}
              {e.label}
            </Text>
          ))}
        </Stack>
      );
    case "step_builder":
      return (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {byOrder(step.options || [], spec.correct_order, "option_id").map((o: Rec, i: number) => (
            <Text key={o.option_id}>
              {i + 1}. {o.payload}
            </Text>
          ))}
        </Stack>
      );
    case "analogy_completion":
      return (
        <Box sx={{ mt: 1 }}>
          <Text>
            {spec.term_a} → {spec.term_b} :: {spec.term_c} → <b>{spec.term_d}</b>
            {spec.relation ? ` (${spec.relation})` : ""}
          </Text>
          <Options options={step.options || []} />
        </Box>
      );
    case "concept_map":
      return (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {(spec.candidate_edges || []).map((e: Rec) => {
            const node = (id: string) => (spec.nodes || []).find((n: Rec) => n.id === id)?.label ?? id;
            return (
              <Stack key={e.id} direction="row" spacing={1} alignItems="flex-start">
                {e.is_correct ? <CheckIcon color="success" fontSize="small" /> : <Box sx={{ width: 20 }} />}
                <Box>
                  <Text>
                    {node(e.from_id)} — {e.label} → {node(e.to_id)}
                  </Text>
                  {e.rationale && <Text muted>{e.rationale}</Text>}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      );
    default:
      return Array.isArray(step.options) && step.options.length ? <Options options={step.options} /> : null;
  }
};

export const QuestView: React.FC<{ body: QuestBody | null }> = ({ body }) => {
  if (!body) {
    return (
      <Typography variant="body2" color="text.secondary">
        Quête introuvable dans le journal (génération jamais servie).
      </Typography>
    );
  }
  const chain: Rec[] = Array.isArray(body.question_chain) ? body.question_chain : [];
  return (
    <Box>
      <Section title="Accroche">
        <Text variant="body1">{body.narrative_hook}</Text>
      </Section>
      {chain.map((step, i) => (
        <Section key={step.step_id ?? i} title={`Étape ${i + 1} · ${step.render_type}`}>
          <Text variant="body1">{step.prompt}</Text>
          <Spec step={step} />
          {step.expected_answer_spec?.explanation && (
            <Box sx={{ mt: 1 }}>
              <Text muted>Explication : {step.expected_answer_spec.explanation}</Text>
            </Box>
          )}
        </Section>
      ))}
      <Section title="Débrief">
        <Text>{body.narrative_debrief}</Text>
        {body.narrative_debrief_success && <Text muted>Réussite : {body.narrative_debrief_success}</Text>}
        {body.narrative_debrief_failure && <Text muted>Échec : {body.narrative_debrief_failure}</Text>}
      </Section>
    </Box>
  );
};

export default QuestView;
